import React from 'react';
import { useHistoricalPlaybackStore } from '../../store/historicalPlaybackStore';
import { useSettingsStore } from '../../store/settingsStore';
import { exchangeLabel, flowOutOfZone, otherZone, type CouplingFlow, type ZoneCoupling } from '../../types/coupling';
import { loadingBarClass, loadingTextClass } from '../../utils/loadingStyles';
import { fmt } from '../../utils/numberFormat';
import { parseUtcTimestamp } from '../../utils/timeUtils';

interface CouplingFlowFiguresProps {
  coupling: ZoneCoupling;
  /** Figures of the owner zone's load flow for this coupling; undefined = none delivered. */
  flow: CouplingFlow | undefined;
  activeZoneId: string;
  /**
   * `popup` renders the full block on Leaflet's white popup (no dark variants); `row` is one
   * compact line for a panel row: loading mini bar, current and the power with a short
   * direction word (the row's title already names the neighbour zone).
   */
  variant: 'popup' | 'row';
}

/**
 * Load-flow figures of a Trennstelle (doc/Architektur/NETZVERBUND.md 6): loading of the coupling
 * cable, current at the Kuppelpunkt and the active power crossing the Trennstelle with its
 * direction seen from the active zone. The cable belongs to the neighbour zone, so these values
 * come from `couplingFlows` of /powercalculation, not from this zone's segments.
 */
export const CouplingFlowFigures: React.FC<CouplingFlowFiguresProps> = ({ coupling, flow, activeZoneId, variant }) => {
  const historical = useHistoricalPlaybackStore(s => s.mode) !== 'live';
  const thresholds = useSettingsStore(s => s.thresholds.cableLoadLevels);
  const neighbour = otherZone(coupling, activeZoneId);
  const onWhite = variant === 'popup';
  const muted = onWhite ? 'text-gray-500' : 'text-gray-500 dark:text-gray-400';
  const strong = onWhite ? 'text-gray-900' : 'text-gray-800 dark:text-gray-100';

  if (!flow) {
    return (
      <div className={`text-[10px] ${muted}`}>
        {historical
          ? `Archiv: Lastfluss des Kuppelkabels liegt bei Zone ${neighbour.name}`
          : `Kein Lastfluss für das Kuppelkabel aus Zone ${neighbour.name}`}
      </div>
    );
  }

  const outKw = flowOutOfZone(flow, activeZoneId);
  const direction = exchangeLabel(outKw, neighbour.name);
  const arrow = outKw == null || Math.abs(outKw) < 0.05 ? '' : outKw > 0 ? '→ ' : '← ';
  const phases = [flow.currentL1A, flow.currentL2A, flow.currentL3A];
  const hasPhases = phases.every(v => v != null);
  const ts = flow.timestamp ? parseUtcTimestamp(flow.timestamp) : null;
  const tsLabel = ts && !Number.isNaN(ts.getTime())
    ? ts.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  if (variant === 'row') {
    const shortDirection = outKw == null || Math.abs(outKw) < 0.05 ? '' : outKw > 0 ? 'Abgabe' : 'Bezug';
    return (
      <div
        className={`flex items-center gap-1.5 text-[10px] ${muted} min-w-0`}
        title={`${direction}${tsLabel ? ` · Lastfluss ${tsLabel}` : ''} (Ergebnis der Zone ${neighbour.name})`}
      >
        {flow.loadingPercent != null && (
          <>
            <span className="w-10 h-1.5 shrink-0 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
              <span className={`block h-full rounded-full ${loadingBarClass(flow.loadingPercent, thresholds)}`} style={{ width: `${Math.min(flow.loadingPercent, 100)}%` }} />
            </span>
            <span className={`shrink-0 font-semibold ${loadingTextClass(flow.loadingPercent, thresholds)}`}>{fmt(flow.loadingPercent, 1)} %</span>
          </>
        )}
        <span className="truncate">
          {flow.currentA != null && <>{fmt(Math.abs(flow.currentA), 1)} A</>}
          {flow.currentA != null && outKw != null && ' · '}
          {outKw != null && (
            <><span className={`font-semibold ${strong}`}>{arrow}{fmt(Math.abs(outKw), 1)} kW</span>{shortDirection ? ` ${shortDirection}` : ' kein Austausch'}</>
          )}
          {flow.currentA == null && outKw == null && 'Leistung unbekannt'}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {flow.loadingPercent != null ? (
        <div>
          <div className="flex items-center justify-between text-xs">
            <span className={muted}>Kabelauslastung</span>
            <span className={`font-bold ${loadingTextClass(flow.loadingPercent, thresholds, true)}`}>{fmt(flow.loadingPercent, 1)} %</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${loadingBarClass(flow.loadingPercent, thresholds)}`} style={{ width: `${Math.min(flow.loadingPercent, 100)}%` }} />
          </div>
        </div>
      ) : (
        <div className={`text-xs ${muted}`}>Auslastung unbekannt</div>
      )}
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        <div className="rounded bg-gray-50 px-2 py-1">
          <div className={`font-semibold ${strong}`}>
            {outKw != null ? <>{arrow}{fmt(Math.abs(outKw), 1)} <span className={`font-normal ${muted}`}>kW</span></> : '–'}
          </div>
          <div className={`text-[10px] ${muted}`}>{direction}</div>
        </div>
        <div className="rounded bg-gray-50 px-2 py-1">
          <div className={`font-semibold ${strong}`}>
            {flow.currentA != null ? <>{fmt(Math.abs(flow.currentA), 1)} <span className={`font-normal ${muted}`}>A</span></> : '–'}
          </div>
          <div className={`text-[10px] ${muted}`}>
            {hasPhases
              ? `L1 ${fmt(Math.abs(phases[0]!), 0)} · L2 ${fmt(Math.abs(phases[1]!), 0)} · L3 ${fmt(Math.abs(phases[2]!), 0)} A`
              : 'Strom an der Trennstelle'}
          </div>
        </div>
      </div>
      <div className={`text-[10px] ${muted}`}>
        {tsLabel ? `Lastfluss ${tsLabel} · ` : ''}Ergebnis der Zone {neighbour.name}
      </div>
    </div>
  );
};
